'use client';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/app/store';
import {
  selectVisibleWorkspaceApps,
  selectWorkspaceIsLoading,
  selectWorkspaceError,
  selectUseCustomWorkspace,
} from '@/app/store/selectors/workspaceSelectors';
import { parseTenantBranding } from '@/app/utils/tenantBranding';
import { useViewport } from '@/app/hooks/useViewport';
import DefaultWorkspaceView from '@/app/features/workspace/views/DefaultWorkspaceView';
import MobileWorkspaceHome from '@/app/features/workspace/views/MobileWorkspaceHome';
import { resolveWorkspaceView } from '@/app/features/workspace/views/registry';

const WorkspacePage = () => {
  const activeTenant = useSelector((s: RootState) => s.tenant.activeTenant);
  const apps = useSelector(selectVisibleWorkspaceApps);
  const isLoading = useSelector(selectWorkspaceIsLoading);
  const error = useSelector(selectWorkspaceError);
  const useCustomWorkspace = useSelector(selectUseCustomWorkspace);
  const { isMobile, width } = useViewport();

  const branding = useMemo(
    () => (activeTenant ? parseTenantBranding(activeTenant.configurationJson) : {}),
    [activeTenant]
  );

  // Big-tech mainstream pattern (Shopify / Linear / GitHub / Notion / Slack):
  // per-tenant customization applies on desktop only; mobile always renders
  // the shared uniform layout. A tenant-registered custom view is only
  // considered at ≥ md viewport.
  const ResolvedDesktopView = useMemo(() => {
    if (!activeTenant) return DefaultWorkspaceView;
    const custom = resolveWorkspaceView(activeTenant.tenantCode, useCustomWorkspace);
    return custom ?? DefaultWorkspaceView;
  }, [activeTenant, useCustomWorkspace]);

  if (!activeTenant) return null;

  const viewProps = {
    tenant: activeTenant,
    apps,
    branding,
    isLoading,
    error,
    onRetry: () => {},
  };

  // First-paint skeleton while useViewport resolves — matches the pattern
  // previously housed in DefaultWorkspaceView.
  if (width === null) {
    return (
      <div className="max-w-4xl space-y-6">
        <div className="h-8 w-2/3 rounded bg-muted animate-pulse" />
        <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
      </div>
    );
  }

  if (isMobile) return <MobileWorkspaceHome {...viewProps} />;

  return <ResolvedDesktopView {...viewProps} />;
};

export default WorkspacePage;
